/**
 * Parse & validate the three shapes a HangarMax import can arrive in:
 *
 *  1. Admin export envelope, `kind: 'layout'`   — one layout row.
 *  2. Admin export envelope, `kind: 'location'` — every layout of a location
 *     (including soft-deleted ones; the caller picks).
 *  3. A bare `SceneData` — what a share link serves. No envelope, no item_cache.
 *
 * Unknown keys are stripped by the schemas, which is safe because the caller
 * always persists the ORIGINAL text/object alongside the parsed result.
 */

import { z } from 'zod'
import type {
  HmExportEnvelope,
  HmItemCache,
  HmLayoutRow,
  HmSceneData,
  ImportSourceMeta,
} from './types'

// ----------------------------------------------------------------------------
// Schemas
// ----------------------------------------------------------------------------

const anchorSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  bearing: z.number().nullish(),
})

const doorSchema = z.object({
  wall: z.enum(['front', 'back', 'left', 'right']),
  width_m: z.number(),
  height_m: z.number(),
  offset_m: z.number().nullish(),
  type: z.enum(['bifold', 'sliding', 'rolling', 'hydraulic', 'personnel']).nullish(),
  open_direction: z.string().nullish(),
})

const buildingSchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
  type: z.enum(['box', 't_hangar']),
  width_m: z.number(),
  depth_m: z.number(),
  height_m: z.number(),
  bearing: z.number().nullish(),
  doors: z.array(doorSchema).nullish(),
  scene_position: z.object({ x: z.number(), z: z.number() }).nullish(),
  wall_thickness_in: z.number().nullish(),
  style: z.string().nullish(),
  dimension_mode: z.string().nullish(),
  t_hangar_config: z
    .object({
      wing_width_m: z.number().nullish(),
      wing_depth_m: z.number().nullish(),
      tail_width_m: z.number().nullish(),
      tail_depth_m: z.number().nullish(),
    })
    .nullish(),
  wall_texture: z.string().nullish(),
  floor_texture: z.string().nullish(),
  door_texture: z.string().nullish(),
  doors_open: z.boolean().nullish(),
})

const itemSchema = z.object({
  id: z.string(),
  type: z.enum(['aircraft', 'vehicle', 'equipment', 'structural', 'custom', 'annotation']),
  catalog_id: z.string().nullish(),
  label: z.string().nullish(),
  position: z.object({ x: z.number(), y: z.number().nullish(), z: z.number() }),
  rotation: z.number().nullish(),
  parent_building: z.string().nullish(),
  overrides: z
    .object({
      width_m: z.number().nullish(),
      depth_m: z.number().nullish(),
      height_m: z.number().nullish(),
      color: z.string().nullish(),
      procedural_shape: z.string().nullish(),
      annotation_type: z.string().nullish(),
    })
    .nullish(),
})

const sceneDataSchema = z.object({
  version: z.number().nullish(),
  anchor: anchorSchema,
  buildings: z.array(buildingSchema).nullish(),
  items: z.array(itemSchema).nullish(),
  camera: z.unknown().nullish(),
  mapCenter: z.unknown().nullish(),
  mapZoom: z.number().nullish(),
  mapBearing: z.number().nullish(),
  mapPitch: z.number().nullish(),
  mapStyle: z.string().nullish(),
  groundTexture: z.string().nullish(),
  airportCode: z.string().nullish(),
  locationAddress: z.string().nullish(),
  flythroughs: z.unknown().nullish(),
})

const itemCacheEntrySchema = z.object({
  m: z.string().nullish(),
  w: z.number().nullish(),
  l: z.number().nullish(),
  h: z.number().nullish(),
  mt: z.string().nullish(),
  ps: z.string().nullish(),
  pc: z.string().nullish(),
})

const layoutRowSchema = z.object({
  id: z.string().nullish(),
  name: z.string().nullish(),
  scene_data: sceneDataSchema,
  item_cache: z.record(z.string(), itemCacheEntrySchema).nullish(),
  deleted_at: z.string().nullish(),
})

const envelopeSchema = z.object({
  export_version: z.number().nullish(),
  kind: z.enum(['layout', 'location']).nullish(),
  exported_at: z.string().nullish(),
  exported_by: z.string().nullish(),
  source: z.object({ project_ref: z.string().nullish() }).nullish(),
  location: z.record(z.string(), z.unknown()).nullish(),
  layouts: z.array(layoutRowSchema).nullish(),
})

// ----------------------------------------------------------------------------
// Public API
// ----------------------------------------------------------------------------

export interface ParsedLayoutOption {
  /** HangarMax `layouts.id`, when present. */
  layoutId: string | null
  name: string
  deleted: boolean
  sceneData: HmSceneData
  itemCache: HmItemCache | null
  /** Number of buildings / items, for the layout picker. */
  buildingCount: number
  itemCount: number
}

export interface ParsedBundle {
  kind: 'layout' | 'location' | 'share_scene'
  layouts: ParsedLayoutOption[]
  projectRef: string | null
  exportVersion: number | null
  /** The original payload, verbatim — persisted for lossless preservation. */
  rawEnvelope: unknown
  /** Location row from the envelope, when present (informational only). */
  sourceLocation: Record<string, unknown> | null
}

export class HangarmaxParseError extends Error {
  readonly issues: string[]

  constructor(message: string, issues: string[] = []) {
    super(message)
    this.name = 'HangarmaxParseError'
    this.issues = issues
  }
}

function issuesOf(error: z.ZodError): string[] {
  return error.issues.slice(0, 8).map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
}

function toOption(row: HmLayoutRow, fallbackName: string): ParsedLayoutOption {
  return {
    layoutId: row.id ?? null,
    name: row.name?.trim() || fallbackName,
    deleted: Boolean(row.deleted_at),
    sceneData: row.scene_data,
    itemCache: row.item_cache ?? null,
    buildingCount: row.scene_data.buildings?.length ?? 0,
    itemCount: row.scene_data.items?.length ?? 0,
  }
}

/**
 * Detect and validate any of the three input shapes.
 *
 * @param payload Already-JSON-parsed input.
 */
export function parseHangarmaxPayload(payload: unknown): ParsedBundle {
  if (payload === null || typeof payload !== 'object') {
    throw new HangarmaxParseError('Not a HangarMax export: expected a JSON object.')
  }

  const record = payload as Record<string, unknown>

  // Shape 3 — bare SceneData (share snapshot): has `anchor`, no `layouts`.
  if ('anchor' in record && !('layouts' in record)) {
    const parsed = sceneDataSchema.safeParse(record)
    if (!parsed.success) {
      throw new HangarmaxParseError(
        'This looks like a HangarMax scene but failed validation.',
        issuesOf(parsed.error)
      )
    }
    return {
      kind: 'share_scene',
      layouts: [
        toOption(
          { id: null, name: null, scene_data: parsed.data as HmSceneData, item_cache: null },
          'Shared layout'
        ),
      ],
      projectRef: null,
      exportVersion: null,
      rawEnvelope: null,
      sourceLocation: null,
    }
  }

  // Shapes 1 & 2 — admin export envelope.
  const parsed = envelopeSchema.safeParse(record)
  if (!parsed.success) {
    throw new HangarmaxParseError(
      'Not a recognised HangarMax export (expected an export envelope or a scene).',
      issuesOf(parsed.error)
    )
  }

  const envelope = parsed.data as HmExportEnvelope
  const rows = envelope.layouts ?? []
  if (rows.length === 0) {
    throw new HangarmaxParseError('This export contains no layouts.')
  }

  return {
    kind: envelope.kind === 'location' ? 'location' : 'layout',
    layouts: rows.map((row, i) => toOption(row, `Layout ${i + 1}`)),
    projectRef: envelope.source?.project_ref ?? null,
    exportVersion: envelope.export_version ?? null,
    rawEnvelope: payload,
    sourceLocation: envelope.location ?? null,
  }
}

/** Convenience wrapper for a file upload. */
export function parseHangarmaxText(text: string): ParsedBundle {
  let json: unknown
  try {
    json = JSON.parse(text)
  } catch {
    throw new HangarmaxParseError('That file is not valid JSON.')
  }
  return parseHangarmaxPayload(json)
}

/**
 * Build the `hangarmax_import_sources` identity fields for a chosen layout.
 *
 * The table requires at least one of `source_layout_id` / `share_token`. A file
 * containing a bare scene has neither, so the caller passes `fallbackToken`
 * (a hash-derived stand-in) to keep re-uploads of the same file addressing the
 * same source row instead of creating a duplicate every time.
 */
export function sourceMetaFor(
  bundle: ParsedBundle,
  layout: ParsedLayoutOption,
  opts: {
    sourceKind: 'bundle_upload' | 'share_link'
    shareToken?: string | null
    fallbackToken?: string | null
  }
): ImportSourceMeta {
  const shareToken =
    opts.shareToken ?? (layout.layoutId ? null : opts.fallbackToken ?? null)

  return {
    sourceKind: opts.sourceKind,
    sourceLayoutId: layout.layoutId,
    shareToken,
    sourceProjectRef: bundle.projectRef,
    sourceExportVersion: bundle.exportVersion,
    layoutName: layout.name,
  }
}
